import { useEffect, useRef, useState } from "react";
import { NotificationManager } from "../../../notifications/NotificationManager";
import { useLocation, useNavigate } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import { useApp } from "../../../context/AppContextProvider";
import { useAuth } from "../../../context/UserContextProvider";
import i18n from "../../../language/i18n";
import {
  retrieveTokenInformation,
  retrieveUserInformationFromToken,
  retrieveTokenInformationWithGoogle,
} from "../../../requests/ManagementRequests";
import RightsReserved from "../../../components/common/landing/RightsReserved";
import { PrimaryButton, InputGroup } from "../../../library";
import { loginScreenStyles } from "./LoginScreenStyles";

const LoginScreen = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { setBoarding, isFullScreen, setIsFullScreen } = useApp();
  const { handleStateLogin } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [errorMap, setErrorMap] = useState<any>({
    username: null,
    password: null,
  });
  const loginButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isFullScreen === false) {
      setIsFullScreen(true);
    }
  }, []);

  useEffect(() => {
    if (!state) return;
    const { action, username, password } = state;
    if (action == "LOGIN") {
      handleLogin(username, password);
    }
  }, [state]);

  const checkCommonValidation = () => {
    if (!username || !password) {
      const currentErrorMap = {
        username: null,
        password: null,
      };
      if (!username) {
        currentErrorMap.username = i18n.t("username_required_field");
      }
      if (!password) {
        currentErrorMap.password = i18n.t("password_required_field");
      }
      setErrorMap(currentErrorMap);
      return false;
    }
    return true;
  };

  const handleOnChange = (type, value) => {
    if (type === "username") {
      if (value === null || value === "") {
        setErrorMap((prevErrorMap) => ({
          ...prevErrorMap,
          username: i18n.t("username_required_field"),
        }));
      } else {
        setErrorMap((prevErrorMap) => ({
          ...prevErrorMap,
          username: null,
        }));
      }
      setUsername(value);
    } else if (type === "password") {
      if (value === null || value === "") {
        setErrorMap((prevErrorMap) => ({
          ...prevErrorMap,
          password: i18n.t("password_required_field"),
        }));
      } else {
        setErrorMap((prevErrorMap) => ({
          ...prevErrorMap,
          password: null,
        }));
      }
      setPassword(value);
    }
  };

  const handleLogin = async (username, password) => {
    try {
      const { data, err } = await retrieveTokenInformation({
        username,
        password,
      });
      if (err) {
        NotificationManager.error(i18n.t("invalid_user_credentials_text"));
        return;
      }
      const { accessToken, refreshToken } = data as any;
      const { data: userData, err: userErr } =
        await retrieveUserInformationFromToken(accessToken);
      if (userErr) {
        return;
      }
      if ((userData as any).enabled) {
        handleStateLogin(accessToken, refreshToken, userData, null);
        setBoarding("PROFILE_SELECTION_REQUIRED");
        NotificationManager.success(i18n.t("login_successful_text"));
      } else {
        setBoarding("ACCOUNT_ACTIVATION_REQUIRED");
      }
      navigate("/");
    } catch (err) {
      return;
    }
  };

  const handleLoginOnClick = async () => {
    if (checkCommonValidation()) {
      await handleLogin(username, password);
      localStorage.setItem("appState", "LOGIN");
    }
  };

  const handleOnKeyDownOnClick = (e) => {
    if (e.key === "Enter") {
      // Fire a real click on the login button so its loading state is
      // triggered on Enter, exactly as it is on a mouse click.
      loginButtonRef.current?.click();
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const { credential } = credentialResponse;
      const { data, err } = await retrieveTokenInformationWithGoogle({
        token: credential,
      });
      if (err) {
        NotificationManager.error(i18n.t("google_login_failed"));
        return;
      }
      const { accessToken, refreshToken } = data as any;
      const { data: userData, err: userErr } =
        await retrieveUserInformationFromToken(accessToken);

      if (userErr) {
        NotificationManager.error(i18n.t("google_login_failed"));
        return;
      }

      if ((userData as any).enabled) {
        handleStateLogin(accessToken, refreshToken, userData, null);
        setBoarding("PROFILE_SELECTION_REQUIRED");
        NotificationManager.success(i18n.t("login_successful_text"));
        localStorage.setItem("appState", "LOGIN");
      } else {
        setBoarding("ACCOUNT_ACTIVATION_REQUIRED");
      }
      navigate("/");
    } catch (err) {
      NotificationManager.error(i18n.t("google_login_failed"));
    }
  };

  const handleGoogleError = () => {
    NotificationManager.error(i18n.t("google_login_failed"));
  };

  return (
    <div className="rah-root bg-catchup-pattern-login">
      <style>{loginScreenStyles}</style>
      <div className="rah-wrap">
        <img
          className="rah-mascot"
          src="/mascots/mascot-login.webp"
          alt="CatchUpper"
        />
        <div className="rah-card">
          <div className="rah-inner">
            <img
              className="rah-logo"
              src="/logos/logo-primary-2.webp"
              alt="CatchUpper"
            />
            <p className="rah-tag">{i18n.t("login_tagline")}</p>
            <div className="rah-form">
              <div className="rah-fields">
                <InputGroup
                  type="text"
                  title={i18n.t("username_or_email")}
                  placeholder={i18n.t("username_or_email")}
                  value={username}
                  onChange={(e) => {
                    handleOnChange("username", e.target.value.trim());
                  }}
                  errorText={errorMap["username"]}
                  onKeyDown={handleOnKeyDownOnClick}
                />
                <InputGroup
                  type="password"
                  title={i18n.t("password")}
                  placeholder={i18n.t("password")}
                  value={password}
                  onChange={(e) => {
                    handleOnChange("password", e.target.value.trim());
                  }}
                  errorText={errorMap["password"]}
                  onKeyDown={handleOnKeyDownOnClick}
                />
                <div className="rah-row">
                  <p
                    className="rah-link"
                    onClick={() => {
                      navigate("/forgot-password");
                    }}
                  >
                    {i18n.t("to_forget_password_text")}
                  </p>
                </div>
              </div>

              <PrimaryButton
                ref={loginButtonRef}
                title={i18n.t("login")}
                size="medium"
                block
                textOnly
                onClick={handleLoginOnClick}
              />

              <div className="rah-divider">
                <span>{i18n.t("or")}</span>
              </div>

              <div className="rah-google">
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  theme="outline"
                  size="large"
                  text="signin_with"
                  shape="pill"
                  width="320"
                />
              </div>
            </div>

            <p className="rah-admin">
              {i18n.t("to_admin_text")}{" "}
              <span
                className="rah-link"
                onClick={() => {
                  window.open("https://admin.catchupper.com", "_blank");
                }}
              >
                {i18n.t("to_admin_cta")}
              </span>
            </p>
          </div>
        </div>
        <div className="rah-rights">
          <RightsReserved />
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;
